// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import {
  Sort,
  SortDirection,
  invert as invertDirection,
  sortDirectionSchema,
} from '@/constants/sort';
import { FulfillmentAnalyticsSection } from '@/fulfillment/components/fulfillment-analytics-section';
import { FulfillmentListing } from '@/fulfillment/components/fulfillment-listing';
import {
  FulfillmentSearchListing,
  FulfillmentSortProperty,
  fulfillmentSearchListingSchema,
} from '@/fulfillment/hooks/use-fulfillments';
import { useLocalStorage } from '@/hooks/use-local-storage';
import {
  DEFAULT_CYCLE_TIME_RANGE_DAYS,
  useReportDateRange,
} from '@/report/hooks/use-report-interval';
import { routeDateSchema } from '@/utilities/date-time-schema';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Local storage key for persisting sort preferences.
 */
const LOCAL_STORAGE_SORT_KEY = 'fulfillment-sort';

/**
 * Default sort property when no preference is set.
 */
const DEFAULT_SORT_PROPERTY: FulfillmentSortProperty = 'foNumber';

/**
 * Default sort direction when no preference is set.
 */
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

/**
 * Schema for validating sort preferences.
 */
const sortSchema = z.object({
  property: z.enum(['foNumber', 'status']),
  direction: sortDirectionSchema,
});

const fulfillmentRouteSearchSchema = fulfillmentSearchListingSchema.extend({
  startOn: routeDateSchema,
  endOn: routeDateSchema,
});

/**
 * Route configuration for fulfillment order listing page.
 */
export const Route = createFileRoute('/_app/fulfillments/')({
  component: FulfillmentRoute,
  validateSearch: fulfillmentRouteSearchSchema,
});

/**
 * Main component for the fulfillment order listing page.
 */
function FulfillmentRoute() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { user } = useCurrentUser();
  const cycleTimeDateRange = useReportDateRange({
    start: search.startOn,
    end: search.endOn,
    defaultRangeDays: DEFAULT_CYCLE_TIME_RANGE_DAYS,
  });

  /**
   * Sort preference persistence in local storage.
   */
  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  /**
   * Current sort configuration.
   * Prioritizes URL parameters over local storage preferences.
   */
  const sort: Sort<FulfillmentSortProperty> = {
    property: search.sort ?? sortPreference?.property ?? DEFAULT_SORT_PROPERTY,
    direction:
      search.direction ?? sortPreference?.direction ?? DEFAULT_SORT_DIRECTION,
  };

  /**
   * Updates sort configuration.
   * Saves preference to local storage and updates URL.
   */
  const setSort = (
    property: FulfillmentSortProperty,
    direction: SortDirection
  ) => {
    setSortPreference({ property, direction });
    navigate({ search: (prev) => ({ ...prev, direction, sort: property }) });
  };

  /**
   * Handles column header click for sorting.
   * Toggles direction if same column, otherwise sorts ascending.
   */
  const handleSort = (property: FulfillmentSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invertDirection(sort.direction));
    }
    setSort(property, 'asc');
  };

  const searchParameters: FulfillmentSearchListing = {
    page: search.page,
    query: search.query,
    status: search.status,
    subStatuses: search.subStatuses,
    sort: search.sort,
    direction: search.direction,
    fromLocations: search.fromLocations,
    fromCustomerLocations: search.fromCustomerLocations,
    fromLocationCompanyTypes: search.fromLocationCompanyTypes,
    toLocations: search.toLocations,
    toCustomerLocations: search.toCustomerLocations,
    toLocationCompanyTypes: search.toLocationCompanyTypes,
  };

  const handleCycleTimeDateRangeChange = (dateRange: DateRange<DateTime>) => {
    navigate({
      search: (prev) => ({
        ...prev,
        startOn: dateRange[0]?.toISODate(),
        endOn: dateRange[1]?.toISODate(),
      }),
    });
  };

  return (
    <PageContent>
      <FulfillmentAnalyticsSection
        interval={cycleTimeDateRange.interval}
        timezone={user.organization.timezone}
        isDateRangeInvalid={cycleTimeDateRange.isInvalid}
        onDateRangeChange={handleCycleTimeDateRangeChange}
      />
      <FulfillmentListing
        currentStatus={search.status ?? 'all'}
        searchParameters={searchParameters}
        sort={sort}
        onSort={handleSort}
        navigateFn={(options) => {
          navigate({
            ...options,
            search: {
              ...options.search,
              startOn: search.startOn,
              endOn: search.endOn,
            },
          });
        }}
      />
    </PageContent>
  );
}
